import { Button, Spinner } from "flowbite-react"
import { flatten, groupBy, map, pick } from "lodash"
import moment, { Moment } from "moment"
import React, { useMemo, useState } from "react"

import { StopwatchIcon } from "@assets/icons"
import CalendarIcon from "@icons/Calendar"
import RightArrow from "@icons/RightArrow"
import {
  capitalizeFirstLetter,
  getDateInDomainTimezone,
  phpToMoment,
} from "@mono/essentials/utils/Common"

import { ProductPriceTypeData } from "../Preview/ProductDetailsPreview"

const ProductOverView = ({
  selectedProductDetail,
  selectedPriceTypeData,
  selectedDurationDetails,
  selectedTimeSlot,
  domainDetails,
  handleNextStep,
  isLoading = false,
  selectedAddOnData,
  submitButtonDisabled = false,
}: any) => {
  const addonData = useMemo(() => {
    const grouped = groupBy(selectedAddOnData, "product_id")

    const output = map(grouped, (units, product_id) => ({
      product_name: units[0].product_name,
      product_id,
      units: units.map(({ product_name, product_id, ...unitDetails }) => unitDetails),
    }))
    return output
  }, [selectedAddOnData])

  const calculateTotalPrice = () => {
    let totalPrice = 0

    const transformArray = (arr) => {
      return (arr || [])?.map((item) => ({
        unitCount: item?.unitCount || item?.quantity,
        unit_amount: item.unit_amount,
      }))
    }

    const transformedA = transformArray(selectedPriceTypeData || [])
    const transformedB = transformArray(selectedAddOnData || [])

    const all_products = [...transformedA, ...transformedB]

    const currancy: string | undefined = domainDetails?.currency_symbol
    ;(all_products || []).map((product: any) => {
      const price = product?.unit_amount ?? 0
      return (totalPrice += product.unitCount * price)
    })
    return `${currancy}${totalPrice.toFixed(2)}`
  }

  const gotoNextStep = () => {
    handleNextStep?.()
  }

  return (
    <div className={"pt-5 px-4 pb-6 col-span-2 border border-neutral-450 rounded-lg h-min"}>
      <div className={"flex flex-col gap-3"}>
        <div className={"flex flex-row items-center gap-2"}>
          <span className={"lg:text16Semibold text14Medium text-neutral-900"}>
            {selectedProductDetail?.name}
          </span>
        </div>
        <div className={"w-full border-t border-dashed border-neutral-300"} />
        <div className={"w-full items-center"}>
          <div className={"flex flex-col gap-3"}>
            <div className={"flex items-center"}>
              <div>
                <CalendarIcon className={"mr-2"} />
              </div>
              <div className={"lg:text14Regular text12Regular w-60 text-neutral-900"}>
                {selectedProductDetail?.is_dynamic_rental &&
                selectedProductDetail?.is_multi_day_rental &&
                selectedDurationDetails?.available_option.includes("multi_day") &&
                selectedDurationDetails?.end_date
                  ? `
                  ${moment(selectedDurationDetails?.start_date as Moment).format(
                    phpToMoment(domainDetails?.date_format || "Y-m-d")
                  )} - ${moment(selectedDurationDetails?.end_date as Moment).format(
                    phpToMoment(domainDetails?.date_format || "Y-m-d")
                  )}
                `
                  : selectedProductDetail?.is_dynamic_rental &&
                      selectedDurationDetails?.available_option.includes("single_day")
                    ? `${moment(selectedDurationDetails?.start_date as Moment).format(
                        phpToMoment(domainDetails?.date_format || "Y-m-d")
                      )}`
                    : `
                ${moment(selectedDurationDetails?.date as Moment).format(
                  phpToMoment(domainDetails?.date_format || "Y-m-d")
                )}
                `}
              </div>
            </div>
            {selectedTimeSlot?.start_time && selectedTimeSlot?.end_time && (
              <div className={"flex items-center"}>
                <StopwatchIcon className={"mr-2 text-neutral-150"} />
                <div
                  className={"lg:text14Regular text12Regular w-60 text-neutral-900"}
                >{`${selectedTimeSlot?.start_time} - ${selectedTimeSlot?.end_time}`}</div>
              </div>
            )}
            <div className={"flex border-t border-dashed border-neutral-300"} />
            <div className={"flex w-full flex-wrap items-center gap-2.5"}>
              {(selectedPriceTypeData || []).map(
                (product: ProductPriceTypeData, index: number | string) => (
                  <>
                    <div className={" w-full items-center justify-between"} key={index}>
                      <div className={"text14Medium"}>
                        {capitalizeFirstLetter(
                          product.unitName
                            ? product.unitName
                            : selectedDurationDetails?.unitName
                              ? selectedDurationDetails?.unitName
                              : "".toLocaleLowerCase()
                        )}
                      </div>
                      <div className={"lg:text14Medium text12Medium text-neutral-900"}>
                        <div className={"flex items-center justify-between mt-2"}>
                          <span className={"lg:text14Medium text12Regular text-neutral-500"}>
                            ({product.formatted_unit_price} x{" "}
                            {product.unitCount ?? product?.quantity})
                          </span>
                          <span className={"text14Medium text-neutral-800"}>{`${
                            product?.formatted_price ?? ""
                          }`}</span>
                        </div>
                      </div>
                      <div className={"lg:text14Regular text12Regular  text-neutral-500"} />
                    </div>
                  </>
                )
              )}
            </div>

            {addonData?.map((productItem, addonIndex) => (
              <>
                <div className={"text14Medium"} key={addonIndex}>
                  {productItem.product_name}
                  <span className={"text14Medium text-orange-800"}>{" (Add-ons)"}</span>
                </div>

                <div
                  className={"flex w-full flex-wrap items-center gap-2.5"}
                  key={`addonData-${addonIndex}`}
                >
                  {(productItem?.units || []).map((product: any, index: number | string) => (
                    <>
                      <div className={" w-full items-center justify-between"} key={index}>
                        <div className={"lg:text14Medium text12Medium text-neutral-900"}>
                          <div className={"flex items-center justify-between "}>
                            <div
                              className={
                                "lg:text14Medium text12Regular text-neutral-500 capitalize"
                              }
                            >
                              {product?.unit_name}
                              <span>
                                ({product.formatted_unit_price}x{product?.quantity})
                              </span>
                            </div>
                            <span
                              className={"text14Medium text-neutral-800"}
                            >{`${product?.formatted_price ?? ""}`}</span>
                          </div>
                        </div>
                        <div className={"lg:text14Regular text12Regular  text-neutral-500"} />
                      </div>
                    </>
                  ))}
                </div>
              </>
            ))}
          </div>
        </div>
        <div className={"w-full border-t border-dashed border-neutral-300"} />
        <div className={"flex justify-between mb-3"}>
          <div className={"lg:text16Semibold text14Regular"}>Total</div>
          <div className={"lg:text16Semibold text14Semibold"}>{`${calculateTotalPrice()}`}</div>
        </div>
        {handleNextStep && (
          <Button
            color={"primary"}
            data-testid={"nextButton"}
            className={"w-full [&>span]:!text16Medium [&>span]:!py-[13px] !h-10"}
            onClick={gotoNextStep}
            disabled={submitButtonDisabled || isLoading}
          >
            {isLoading ? (
              <Spinner size={"sm"} light className={"text-primary-400 fill-white"} />
            ) : (
              <>
                Next
                <RightArrow className={"ml-2.5 text-whiteBlack-100"} width={20} height={20} />
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  )
}

export default ProductOverView
